/**

 * Shared materials for the procedural circle stocker — all instances reuse these.

 */

import * as THREE from 'three';

import { getSharedStandardMaterial } from '../../../../../utils/threeResourcePool';

import { getSilverBrushedAluminumMaps } from '../../../../../utils/equipmentCabinetTextures';

import { FAB_COLORS, FAB_BODY_SHADING, FAB_LED_INTENSITY } from '../../rendering/fabColors';

let circleBrushedMaps: ReturnType<typeof getSilverBrushedAluminumMaps> | null = null;

function circleBrushedMapsOnce() {
  if (!circleBrushedMaps) circleBrushedMaps = getSilverBrushedAluminumMaps();
  return circleBrushedMaps;
}

import {
  createFabLightBluePhysicalGlass,

  createFabLightBlueDistantGlass,

  createFabPistaGreenPhysicalGlass,

  createFabShelfSkyBluePhysicalGlass,

  createFabPistaGreenDistantGlass,

  createFabClearWhitePhysicalGlass,

  createFabClearWhiteSidePhysicalGlass,

  createFabClearWhiteDistantGlass,

} from '../../rendering/fabGlassMaterials';



/** Stocker palette aligned with central FAB color system. */

export const CIRCLE_STOCKER_PALETTE = {

  bodyWhite: FAB_COLORS.bodyWhite,

  bodyGray: FAB_COLORS.bodyLight,

  bodyWarm: FAB_COLORS.bodyWarm,

  bodyBright: FAB_COLORS.bodyBright,

  frameGraphite: FAB_COLORS.graphite,

  frameDark: FAB_COLORS.darkGraphite,

  frameInner: FAB_COLORS.frameInner,

  stainless: FAB_COLORS.stainless,

  stainlessDark: FAB_COLORS.metal,

  interior: FAB_COLORS.skyBlueBright,

  shelf: FAB_COLORS.lightBlueGlass,

  glassTint: FAB_COLORS.glass,

  glassBlue: FAB_COLORS.glassBlue,

  smokedGlass: FAB_COLORS.glass,

  sideGlass: FAB_COLORS.glassBlue,

  ledCyan: FAB_COLORS.cyan,

  ledGreen: FAB_COLORS.green,

  ledAmber: FAB_COLORS.amber,

  ledRed: FAB_COLORS.red,

  vent: FAB_COLORS.metal,

  panelSeam: FAB_COLORS.brushedMetal,

  loadPortBody: FAB_COLORS.loadPortBody,

  loadPortRecess: FAB_COLORS.loadPortRecess,

  foupBody: FAB_COLORS.foupBody,

  foupTop: FAB_COLORS.foupTop,

} as const;



/** Reference cleanroom enclosure glass — light transparent white glass. */

export function getCircleGlassMaterial(opacity = 1): THREE.MeshPhysicalMaterial {

  return createFabClearWhitePhysicalGlass(opacity, 'circle-stocker');

}



/** Side accent panels — light white/neutral transparent glass. */

export function getCircleBlueGlassMaterial(opacity = 1): THREE.MeshPhysicalMaterial {

  return createFabClearWhiteSidePhysicalGlass(opacity, 'circle-stocker-blue');

}



export function getCircleGlassDistantMaterial(opacity = 1): THREE.MeshStandardMaterial {

  return createFabClearWhiteDistantGlass(opacity);

}



/** Enclosure glass — front viewing panel (reference cleanroom tint). */

export function getCircleClearGlassMaterial(opacity = 1): THREE.MeshPhysicalMaterial {

  return createFabClearWhitePhysicalGlass(opacity, 'circle-stocker-enclosure');

}



/** Bright white/transparent side panels — clean bright enclosure read. */

export function getCircleClearSideGlassMaterial(opacity = 1): THREE.MeshPhysicalMaterial {

  return createFabClearWhiteSidePhysicalGlass(opacity, 'circle-stocker-enclosure-side');

}



export function getCircleClearSideGlassDistantMaterial(opacity = 1): THREE.MeshStandardMaterial {

  return createFabClearWhiteDistantGlass(opacity);

}



export function getCircleClearGlassDistantMaterial(opacity = 1): THREE.MeshStandardMaterial {

  return createFabClearWhiteDistantGlass(opacity);

}



/** Opaque front-face shell — deep black, not transparent. */

export function getCircleFrontFaceMaterial(opacity = 1): THREE.MeshStandardMaterial {

  return getSharedStandardMaterial({

    color: CIRCLE_STOCKER_PALETTE.frameInner,

    metalness: 0.06,

    roughness: 0.88,

    envMapIntensity: 0.12,

    opacity,

    transparent: opacity < 1,

  });

}



/** Load-port front face — clean transparent white glass. */

export function getCircleLoadPortFaceGlassMaterial(opacity = 1): THREE.MeshPhysicalMaterial {

  return createFabClearWhitePhysicalGlass(opacity, 'circle-stocker-load-port');

}



export function getCircleLoadPortFaceGlassDistantMaterial(opacity = 1): THREE.MeshStandardMaterial {

  return createFabClearWhiteDistantGlass(opacity);

}



/** Light blue transparent shelf / interior glass. */

export function getCircleLightBlueGlassMaterial(opacity = 1): THREE.MeshPhysicalMaterial {

  return createFabLightBluePhysicalGlass(opacity, 'circle-stocker-shelf');

}



export function getCircleLightBlueGlassDistantMaterial(opacity = 1): THREE.MeshStandardMaterial {

  return createFabLightBlueDistantGlass(opacity);

}



/** Pista green transparent shelf glass — rack interiors. */

export function getCirclePistaGreenGlassMaterial(opacity = 1): THREE.MeshPhysicalMaterial {

  return createFabPistaGreenPhysicalGlass(opacity, 'circle-stocker-shelf');

}



export function getCirclePistaGreenGlassDistantMaterial(opacity = 1): THREE.MeshStandardMaterial {

  return createFabPistaGreenDistantGlass(opacity);

}



export function getCircleBodyMaterial(opacity = 1): THREE.MeshStandardMaterial {

  return getSharedStandardMaterial({

    color: CIRCLE_STOCKER_PALETTE.bodyWhite,

    ...FAB_BODY_SHADING.white,

    opacity,

    transparent: opacity < 1,

  });

}



export function getCircleBodyBrightMaterial(opacity = 1): THREE.MeshStandardMaterial {

  return getSharedStandardMaterial({

    color: CIRCLE_STOCKER_PALETTE.bodyBright,

    ...FAB_BODY_SHADING.bright,

    opacity,

    transparent: opacity < 1,

  });

}



export function getCircleLightGrayMaterial(opacity = 1): THREE.MeshStandardMaterial {

  return getSharedStandardMaterial({

    color: CIRCLE_STOCKER_PALETTE.bodyGray,

    ...FAB_BODY_SHADING.lightGray,

    opacity,

    transparent: opacity < 1,

  });

}



export function getCircleFrameMaterial(opacity = 1): THREE.MeshStandardMaterial {

  return getSharedStandardMaterial({

    color: CIRCLE_STOCKER_PALETTE.frameDark,

    metalness: 0.68,

    roughness: 0.24,

    envMapIntensity: 0.58,

    opacity,

    transparent: opacity < 1,

  });

}

/** Brushed satin silver — outer frame, load-port deck, mechanism rails. */
export function getCircleBrushedSilverMaterial(opacity = 1): THREE.MeshStandardMaterial {
  const maps = circleBrushedMapsOnce();
  return getSharedStandardMaterial({
    color: FAB_COLORS.bodySilver,
    map: maps.map,
    roughnessMap: maps.roughnessMap,
    metalness: 0.88,
    roughness: 0.24,
    envMapIntensity: 0.96,
    opacity,
    transparent: opacity < 1,
  });
}



export function getCircleLoadPortBodyMaterial(opacity = 1): THREE.MeshStandardMaterial {

  return getSharedStandardMaterial({

    color: CIRCLE_STOCKER_PALETTE.loadPortBody,

    ...FAB_BODY_SHADING.white,

    opacity,

    transparent: opacity < 1,

  });

}



export function getCircleLoadPortRecessMaterial(opacity = 1): THREE.MeshStandardMaterial {

  return getSharedStandardMaterial({

    color: CIRCLE_STOCKER_PALETTE.loadPortRecess,

    ...FAB_BODY_SHADING.darkGraphite,

    opacity,

    transparent: opacity < 1,

  });

}



export function getCircleStainlessMaterial(opacity = 1): THREE.MeshStandardMaterial {
  return getCircleBrushedSilverMaterial(opacity);
}

/** Brushed silver outer perimeter frame. */
export function getCircleOuterFrameMaterial(opacity = 1): THREE.MeshStandardMaterial {
  return getCircleBrushedSilverMaterial(opacity);
}

/** Premium dark graphite FOUP shell — instanced proxy + detailed mesh. */
export function getCircleFoupShellMaterial(opacity = 1): THREE.MeshStandardMaterial {
  return getSharedStandardMaterial({
    color: FAB_COLORS.foupBody,
    metalness: 0.42,
    roughness: 0.32,
    envMapIntensity: 0.72,
    opacity,
    transparent: opacity < 1,
  });
}

export function getCircleFoupLidMaterial(opacity = 1): THREE.MeshStandardMaterial {
  return getSharedStandardMaterial({
    color: FAB_COLORS.foupTop,
    metalness: 0.38,
    roughness: 0.28,
    envMapIntensity: 0.82,
    opacity,
    transparent: opacity < 1,
  });
}



export function getCircleShelfMaterial(opacity = 1): THREE.MeshStandardMaterial {

  return getCircleLightBlueGlassDistantMaterial(opacity);

}



/** Sky-blue U-channel shelf glass — decks and side/back walls. */
export function getCircleShelfGlassMaterial(opacity = 1): THREE.MeshPhysicalMaterial {

  return createFabShelfSkyBluePhysicalGlass(opacity, 'circle-stocker-shelf');

}

/** Reference L-bracket shelf tabs — light cyan-blue metal. */
export function getCircleShelfBracketMaterial(opacity = 1): THREE.MeshStandardMaterial {

  return getSharedStandardMaterial({

    color: FAB_COLORS.lightBlueGlass,

    metalness: 0.38,

    roughness: 0.32,

    envMapIntensity: 0.78,

    opacity,

    transparent: opacity < 1,

  });

}

/** Dark horizontal shelf rail behind the front LED strip. */
export function getCircleShelfRailMaterial(opacity = 1): THREE.MeshStandardMaterial {

  return getSharedStandardMaterial({

    color: FAB_COLORS.graphite,

    metalness: 0.42,

    roughness: 0.38,

    envMapIntensity: 0.55,

    opacity,

    transparent: opacity < 1,

  });

}



/** Opaque dark-blue panel behind each shelf bay only. */
export function getCircleShelfBackMaterial(opacity = 1): THREE.MeshStandardMaterial {

  return getSharedStandardMaterial({

    color: FAB_COLORS.navyGlassDeep,

    metalness: 0.1,

    roughness: 0.28,

    envMapIntensity: 0.72,

    opacity,

    transparent: opacity < 1,

  });

}



/** White FOUP placement deck on the center aisle / crane cradle. */

export function getCirclePlacementTrayMaterial(opacity = 1): THREE.MeshStandardMaterial {
  const maps = circleBrushedMapsOnce();
  return getSharedStandardMaterial({
    color: FAB_COLORS.placementTray,
    map: maps.map,
    roughnessMap: maps.roughnessMap,
    metalness: 0.62,
    roughness: 0.2,
    envMapIntensity: 0.92,
    opacity,
    transparent: opacity < 1,
  });
}

/** Central vertical rod + floor rail inside the stocker mechanism. */
export function getCircleMechanismRodMaterial(opacity = 1): THREE.MeshStandardMaterial {
  const maps = circleBrushedMapsOnce();
  return getSharedStandardMaterial({
    color: FAB_COLORS.mechanismRod,
    map: maps.map,
    roughnessMap: maps.roughnessMap,
    metalness: 0.58,
    roughness: 0.18,
    envMapIntensity: 0.94,
    opacity,
    transparent: opacity < 1,
  });
}



export function getCircleInteriorMaterial(opacity = 1): THREE.MeshStandardMaterial {

  return getSharedStandardMaterial({

    color: CIRCLE_STOCKER_PALETTE.interior,

    metalness: 0.08,

    roughness: 0.2,

    envMapIntensity: 0.86,

    opacity,

    transparent: opacity < 1,

  });

}



export function getCircleLedMaterial(

  color: string,

  intensity: number,

  opacity = 1

): THREE.MeshStandardMaterial {

  return getSharedStandardMaterial({

    color,

    emissive: color,

    emissiveIntensity: intensity,

    metalness: 0.05,

    roughness: 0.38,

    toneMapped: false,

    opacity,

    transparent: opacity < 1,

  });

}



export function getCircleCyanEdgeMaterial(opacity = 1): THREE.MeshStandardMaterial {
  return getCircleLedMaterial('#94A3B8', FAB_LED_INTENSITY.cyanSoft, opacity);
}



export function statusLedColor(status: string): { color: string; intensity: number } {

  switch (status) {

    case 'running':

      return { color: CIRCLE_STOCKER_PALETTE.ledGreen, intensity: FAB_LED_INTENSITY.green };

    case 'warning':

      return { color: CIRCLE_STOCKER_PALETTE.ledAmber, intensity: FAB_LED_INTENSITY.amber };

    case 'error':

      return { color: CIRCLE_STOCKER_PALETTE.ledRed, intensity: FAB_LED_INTENSITY.red };

    case 'offline':

      return { color: '#4A5560', intensity: 0.1 };

    default:

      return { color: CIRCLE_STOCKER_PALETTE.ledAmber, intensity: FAB_LED_INTENSITY.amber * 0.85 };

  }

}


